import { memo } from "react";
import { getThemeSource } from "src/duck/selectors";
import { Lottie } from "src/components/Lottie";
import {
  skinColors,
  dress,
  hairs,
  accessories,
  noses,
  cheeks,
  eyes
} from "src/modules/avatar/baby-customization";
import { useSelector } from "react-redux";

function BabyLottieAnimation({
  animationName,
  sex = "male",
  skinColor = "FITZ-2",
  width,
  height,
  dressColor = "#2aa598",
  accessoryColor = "#2aa598",
  hairColor = "#4e3727",
  clothes = "NAPPY",
  accessory = "NO_ACCESSORY",
  hair = "HAIR_0",
  nose = "NOSE_0",
  cheek = "CHEEK_2",
  eye = "EYE_0",
  getAnimControls,
  previewMode,
  ...rest
}) {
  useSelector(getThemeSource);

  const stylesToFilter = {
    dressColor,
    accessoryColor,
    hairColor,
    clothes,
    accessory,
    hair,
    nose,
    cheek,
    eye
  };

  const skinEntries = Object.entries(skinColors[skinColor]);
  const isSleepingAnimation = animationName === "sleeping.json";
  const styles = Object.entries({
    ...dress,
    ...hairs,
    ...accessories,
    ...noses,
    ...cheeks,
    ...eyes
  });

  function getColor(prop, isDotColor) {
    if (
      Object.values(dress)
        .flatMap((val) => val)
        .includes(prop)
    ) {
      if (typeof dressColor === "object") {
        return isDotColor ? dressColor[1] : dressColor[0];
      }
      return isDotColor ? "transparent" : dressColor;
    }
    if (
      Object.values(hairs)
        .flatMap((val) => val)
        .includes(prop)
    ) {
      return hairColor;
    }
    if (
      Object.values(accessories)
        .flatMap((val) => val)
        .includes(prop)
    ) {
      return accessoryColor;
    }
  }

  let css = skinEntries.reduce((result, entry) => {
    result =
      result +
      `
          & .${entry[0]} {
            ${
              entry[0] === "AdditionalSkin" && isSleepingAnimation
                ? "stroke"
                : "fill"
            }: ${entry[1]};
          }
        `;
    return result;
  }, "");

  css = styles.reduce((result, entry) => {
    const eyeLayers = ["Eyes0Base", "Eyes1Base", "Eyes2Base"];
    const strokes = isSleepingAnimation
      ? [
          "Nose0Base",
          "Nose1Base",
          "Nose2Base",
          ...eyeLayers.filter((e) => e !== "Eyes2Base")
        ]
      : ["Nose0Base", "Nose1Base", "Nose2Base"];
    const cheekLayers = ["Cheeks0Base", "Cheeks1Base", "Cheeks2Base"];

    let style = entry[1].reduce((res, key) => {
      const isFrogOrBear = key.includes("Frog") || key.includes("Bear");
      const isEyeLayer = eyeLayers.includes(key);
      const isStroke = strokes.includes(key);
      const isCheek = cheekLayers.includes(key);
      let color;
      let toStyle = "fill";
      let opacity = 0;
      let attributes = "";

      if (Object.values(stylesToFilter).includes(entry[0])) {
        if (isStroke) {
          color = skinColors[skinColor].AdditionalSkin;
          toStyle = "stroke";
        }
        if (isCheek) color = skinColors[skinColor].DarkSkin;
        if (isEyeLayer) color = skinColors[skinColor].AdditionalSkin;
        opacity = 1;
        attributes =
          key.includes("Base") && !isFrogOrBear
            ? `${toStyle}: ${color || getColor(key, false)};`
            : key.includes("Dots") && !isFrogOrBear
            ? `${toStyle}: ${color || getColor(key, true)};`
            : "";
      }
      res =
        res +
        `
          & .${key} {
            opacity: ${opacity};
            ${attributes}
          }`;
      return res;
    }, "");
    result = result + style;
    return result;
  }, css);

  return (
    <Lottie
      css={css}
      width={width}
      height={height}
      animationName={animationName}
      getAnimControls={getAnimControls}
      {...rest}
    />
  );
}

export default memo(BabyLottieAnimation);
